import React, { useState, useEffect, useReducer } from 'react'
import Button from '@mui/material/Button'
import DeleteIcon from '@mui/icons-material/Delete'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

function FactSourceColumns(props) {
    const [rangeType, setRangeType] = useState('flexible-range')
    const [namedColumns, setNamedColumns] = useState(false)
    const [factsColumnRanges, setFactsColumnRanges] = useState([])
    const [selectedColumnRangeIndex, setSelectedColumnRangeIndex] = useState(-1)
    const [currentTab, setCurrentTab] = useState(0)
    const [tabData, setTabData] = useState([])
    const [selectedColumns, setSelectedColumns] = useState([])
    const [headerRow, setHeaderRow] = useState(0)
    const [factsHighlightColumn, setFactsHighlightColumn] = useState([])
    const [tableHasHeader, setTableHasHeader] = useState(false)
    const [, forceUpdate] = useReducer(x => x + 1, 0)

    // trigger on component mount
    useEffect(() => {
        setNamedColumns(props.tableDefinition.namedColumns)
        setTabData(props.previewData)
        setHeaderRow(props.headerRow)
        setTableHasHeader(props.tableHasHeader)

        if (props.tableDefinition.namedColumns) {
            setRangeType('named-columns')
        }

        // Create some facts highlights from the default data provided
        let tabIndexes = []
        for (let dataIndex = 0; dataIndex < props.previewData.length; dataIndex++) {
            let definitionRow = getDefinitionRow(dataIndex, props.previewData, props.headerRow)
            tabIndexes.push(buildFactsHighlightColumns(definitionRow, props.factsColumnRanges))
        }
        setFactsHighlightColumn(tabIndexes)

        if (props.factsColumnRanges.length !== factsColumnRanges.length) {
            setFactsColumnRanges(props.factsColumnRanges)
        }
    }, [])

    useEffect(() => {
        setNamedColumns(props.tableDefinition.namedColumns)
    }, [props.tableDefinition.namedColumns, props.tableDefinition.tableHasHeader])
   
    useEffect(() => {
        if (props.headerRow !== headerRow) {
            setHeaderRow(props.headerRow)
        }
    }, [props.headerRow])

    useEffect(() => {
        if (props.factsColumnRanges.length !== factsColumnRanges.length) {
            setFactsColumnRanges(props.factsColumnRanges)
            
            // Create some facts highlights from the default data provided
            let tabIndexes = []
            for (let dataIndex = 0; dataIndex < props.previewData.length; dataIndex++) {
                let definitionRow = getDefinitionRow(dataIndex, props.previewData, props.headerRow)
                tabIndexes.push(buildFactsHighlightColumns(definitionRow, props.factsColumnRanges))
            }
            setFactsHighlightColumn(tabIndexes)
        }
    }, [props.factsColumnRanges])

    useEffect(() => {
        if (props.tableHasHeader !== tableHasHeader) {
            setTableHasHeader(props.tableHasHeader)
        }
    }, [props.tableHasHeader])

    /**
     * getColumnRangeString
     * * Equivalent of the ToString method from the C# class when giving range representation
     * @param {object} range - Range object
     * @returns {string} Name of object
    */
    function getColumnRangeString(range) {
        if (range["absolute"]) {
            if (range["leftIsAnchored"] && range["rightIsAnchored"]) {
                return `L Anchor:${range["left"] + 1}; R Anchor:${range["tableWidth"] - range["right"]}`
            } else if  (range["leftIsAnchored"]) {
                return `L Anchor:${range["left"] + 1}; Width:${range["right"] - range["left"] + 1}`
            } else {
                return `R Anchor:${range["tableWidth"] - range["right"]}; Width:${range["right"] - range["left"] + 1}`
            }
        } else {
            return range["name"]
        }
    }

    /**
     * removeRangeAtIndex
     * * Removes the column range from the array at the specified index
     * @param {int} index - Index to remove
     * @returns {Array} Changed column ranges
    */
    function removeRangeAtIndex(index) {
        let copiedRange = []

        for (let x = 0; x < factsColumnRanges.length; x++) {
            if (x !== index) {
                copiedRange.push(factsColumnRanges[x])
            }
        }

        return copiedRange
    }
    
    function renderTableOrMessageStatingNothingWasSelected() {
        if (tabData.length > 0) {
            return <Tabs value={currentTab} onChange={(event, newValue) => setCurrentTab(newValue)} aria-label="Preview Data Tabs" variant="scrollable">
                {tabData.map((tab, index) => {return <Tab key={`tab-data-${index}`} label={tab["tabName"]} {...a11yProps(index)} />})}
            </Tabs>
        }

        return null
    }

    function renderSelectedPreviewDataPanel() {
        if (tabData.length > currentTab) {
            return <TableContainer component={Paper} style={{ overflowY: "none", "cursor": "pointer", "height":"975px" }}>
                <Table style={{ width: "100%" }} size="small" aria-label="preview data">
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            {tabData[currentTab].previewData[0].map((tabInfo, tabInfoIndex) => {
                                return <TableCell key={`row-excel-header-${tabInfoIndex}`} align="center">{props.getExcelColumnName(tabInfoIndex + 1)}</TableCell>
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tabData[currentTab].previewData.map((tabInfo, tabInfoIndex) => {
                            return (tableHasHeader && props.doesThisRowHaveDataInEverySingleColumn(tabInfo)) || !tableHasHeader ? <TableRow
                                key={`tab-${currentTab}-row-${tabInfoIndex}`}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell>{(tabInfoIndex-getTabsRowOffset(tableHasHeader, tabData[currentTab].previewData))+1}</TableCell>
                                    {renderPreviewRow(tabInfo, tabInfoIndex, currentTab)}
                            </TableRow> : null
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        }

        return null
    }

    /**
     * updateSelectedColumns
     * * When a user makes a selection, check some things and then set the new values
     * @param {int} index - What index was selected
    */
    function updateSelectedColumns(index) {
        let needToInsert = true
        let foundIndex = -1
        let copiedSelectedColumns = selectedColumns

        for (let x = 0; x < selectedColumns.length; x++) {
            if (selectedColumns[x] === index) {
                needToInsert = false
                foundIndex = x
            }
        }

        if (needToInsert) {
            copiedSelectedColumns.push(index)
        } else if (foundIndex > -1) {
            copiedSelectedColumns.splice(foundIndex, 1);
        }

        setSelectedColumns(copiedSelectedColumns)
    }

    /** 
     * renderPreviewRow
     * * Render the row on the preview data tab
     * @param {Array} tabInfo - Preview Data
     * @param {int} rowIndex - Current row index
    */
    function renderPreviewRow(tabInfo, rowIndex, tabIndex) {
        return tabInfo.map((data, index) => {
            return <TableCell
                key={`row-${rowIndex}-cell-${index}`}
                align="center"
                style={{ "backgroundColor" :
                    (selectedColumns && selectedColumns.includes(index)) ? props.uiColors.GRID_HIGHLIGHT :
                    (factsHighlightColumn && factsHighlightColumn[tabIndex] && factsHighlightColumn[tabIndex].includes(index) ? props.uiColors.FACT_COLUMN_HIGHLIGHT : props.uiColors.REGULAR)
                }}
                onClick={(e) => {
                e.preventDefault()
                updateSelectedColumns(index)
                forceUpdate()
            }}>
                {data}
            </TableCell>
        })
    }

    /**
     * getDefinitionRow
     * * Gets the current definition/heder row of the preview data
     * @param {int} tabIndex - Which tab index are we getting the data from?
     * @param {boolean} hasHeader - Does the table(s) have header rows
     * @param {int} firstRow - First row of each table
    */
    function getDefinitionRow(tabIndex, previewData, firstRow) {
        let rtnVal = []
        let firstRowIndex = firstRow
        
        if (firstRowIndex >= 0) {
            for (let x = 0; x < previewData.length; x++) {
                if (x === tabIndex) {
                    rtnVal = previewData[tabIndex]["previewData"][firstRowIndex]
                }
            }
        }
        
        return rtnVal
    }

    /**
     * buildFactsHighlightColumns
     * * Build up the columns highlights by facts from previous selections
     * @param {Array} definitionRow - First/header row of data
     * @param {Array} colRanges - All current column ranges
     * @returns {Array} All indexes that are highlighted in facts
    */
    function buildFactsHighlightColumns(definitionRow, colRanges) {
        let allRanges = []

        for (let rangeIndex = 0; rangeIndex < colRanges.length; rangeIndex++) {
            let currentRange = colRanges[rangeIndex]

            if (currentRange["absolute"]) {
                if (currentRange["leftIsAnchored"] && currentRange["rightIsAnchored"]) {
                    for (let definitionIndex = currentRange["left"]; definitionIndex <= currentRange["right"]; definitionIndex++) {
                        if (definitionRow.length > definitionIndex) {
                            allRanges.push(definitionIndex)
                        }
                    }
                } else if  (currentRange["leftIsAnchored"]) {
                    let anchor = currentRange["left"]
                    let width = currentRange["right"] - currentRange["left"]
                    let itr = 0

                    while (itr <= width) {
                        allRanges.push(anchor)
                        anchor++
                        itr++
                    }
                } else {
                    let rAnchor = currentRange["tableWidth"] - currentRange["right"]
                    let width = currentRange["right"] - currentRange["left"] + 1
                    let itr = 0

                    while (itr < width) {
                        if (definitionRow.length > rAnchor && rAnchor >= -1) {
                            allRanges.push(rAnchor)
                        }
                        rAnchor--
                        itr++
                    }
                }
            } else {
                for (let definitionIndex = 0; definitionIndex < definitionRow.length; definitionIndex++) {
                    if (currentRange["name"] === definitionRow[definitionIndex]) {
                        allRanges.push(definitionIndex)
                    }
                }
            }
        }

        return allRanges
    }

    /**
     * getClickedRange
     * * Get the left and right indexes of what the user selected
     * @param {int[]} selection - What the user selected
     * @returns {Object} Left and right indexes
    */
    function getClickedRange(selection) {
        let rtnVal = {
            "left": -1,
            "right": -1
        }

        for (let x = 0; x < selection.length; x++) {
            if (rtnVal["left"] === -1 || selection[x] < rtnVal["left"]) {
                rtnVal["left"] = selection[x]
            }

            if (rtnVal["right"] === -1 || selection[x] > rtnVal["right"]) {
                rtnVal["right"] = selection[x]
            }
        }

        return rtnVal
    }

    /**
     * getTabsRowOffset
     * * 
     * @param {boolean} hasHeader - Do the tables have a header? 
     * @param {Array} tabPreviewData - Preview data from the source 
     * @returns {int} First row header
     */
    function getTabsRowOffset(hasHeader, tabPreviewData) {
        // Only run this if we determine the table has a header
        if (hasHeader) {
            for (let x = 0; x < tabPreviewData.length; x++) {
                if (props.doesThisRowHaveDataInEverySingleColumn(tabPreviewData[x])) {
                    return x
                }
            }
        }

        return 0
    }

    /**
     * fireOnSaveEvent
     * * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {Array} ranges - All built column ranges
     */
    function fireOnSaveEvent(ranges) {
        if (props.onSave) {
            props.onSave({ "factsColumnRanges": ranges })
        }
    }

    return <>
        <FormGroup>
            <RadioGroup
                row
                defaultValue="flexible-range"
                name="radio-buttons-group"
                onChange={(e) => {
                    setRangeType(e.target.value)
                }}
            >
                <FormControlLabel value="flexible-range" control={<Radio checked={rangeType==='flexible-range'} />} label="Flexible Range" />
                <FormControlLabel value="left-anchored" control={<Radio checked={rangeType==='left-anchored'} />} label="Fixed Width: Left Anchored" />
                <FormControlLabel value="right-anchored" control={<Radio checked={rangeType==='right-anchored'} />} label="Fixed Width: Right Anchored" />
                {namedColumns === true ? <FormControlLabel value="named-columns" control={<Radio checked={rangeType==='named-columns'} />} label="Named Columns" /> : null}
            </RadioGroup>
        </FormGroup>
        <Typography variant="body1" style={{ "margin": "15px 0px" }}>You have selected 0 range(s) containing facts.</Typography>
        <Grid container spacing={2}>
            <Grid item xs={2}>
                <Typography variant="body1">Column Ranges</Typography>
                <List dense={true}>
                    {factsColumnRanges.map((range, index) => {
                        return <ListItem key={`col-range-${index}`} style={{ "backgroundColor": (selectedColumnRangeIndex === index ? "#E0E0E0" : "#FFF") }}
                            secondaryAction={
                                <IconButton edge="end" aria-label="delete" onClick={(e) => {
                                    e.preventDefault()

                                    // Un-selected all column ranges
                                    setSelectedColumnRangeIndex(-1)

                                    // Update the column ranges with the removed index
                                    let alteredRanges = removeRangeAtIndex(index)
                                    setFactsColumnRanges(alteredRanges)
                                    fireOnSaveEvent(alteredRanges)

                                    // Reset the columns highlighted
                                    let tabIndexes = []
                                    for (let dataIndex = 0; dataIndex < tabData.length; dataIndex++) {
                                        let definitionRow = getDefinitionRow(dataIndex, tabData, headerRow)
                                        tabIndexes.push(buildFactsHighlightColumns(definitionRow, alteredRanges))
                                    }
                                    setFactsHighlightColumn(tabIndexes)
                                }}>
                                    <DeleteIcon />
                                </IconButton>
                            }>
                                <ListItemButton onClick={(e) => {
                                    e.preventDefault()
                                    setSelectedColumnRangeIndex(index)

                                    if (range["absolute"]) {
                                        if (range["leftIsAnchored"] && range["rightIsAnchored"]) {
                                            setRangeType('flexible-range')
                                        } else if  (range["leftIsAnchored"]) {
                                            setRangeType('left-anchored')
                                        } else {
                                            setRangeType('right-anchored')
                                        }
                                    } else {
                                        setRangeType('named-columns')
                                    }
                                }}>
                                    <ListItemText primary={getColumnRangeString(range)}/>
                                </ListItemButton>
                            </ListItem>
                    })}
                </List>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'center' }}>
                <Button className="btn" variant="contained" style={{ "marginTop": "25px" }} onClick={(e) => {
                    e.preventDefault()

                    let copiedRanges = factsColumnRanges
                    let selected = getClickedRange(selectedColumns)

                    if (selected["left"] > -1 && selected["right"] > -1) {
                        if (rangeType === 'named-columns') {
                            let definitionRow = getDefinitionRow(currentTab, tabData, headerRow)

                            for (let x = 0; x < selectedColumns.length; x++) {
                                copiedRanges.push({ 'name': definitionRow[selectedColumns[x]], 'left': -1, 'right': -1, 'tableWidth': -1, 'leftIsAnchored': false, 'rightIsAnchored': false, 'absolute': false })
                            }
                        } else if (rangeType === 'flexible-range') {
                            copiedRanges.push({ 'name': '', 'left': selected["left"], 'right': selected["right"], 'tableWidth': tabData[currentTab].previewData[headerRow].length, 'leftIsAnchored': true, 'rightIsAnchored': true, 'absolute': true })
                        } else if (rangeType === 'left-anchored') {
                            copiedRanges.push({ 'name': '', 'left': selected["left"], 'right': selected["right"], 'tableWidth': tabData[currentTab].previewData[headerRow].length, 'leftIsAnchored': true, 'rightIsAnchored': false, 'absolute': true })
                        } else {
                            copiedRanges.push({ 'name': '', 'left': selected["left"], 'right': selected["right"], 'tableWidth': tabData[currentTab].previewData[headerRow].length, 'leftIsAnchored': false, 'rightIsAnchored': true, 'absolute': true })
                        }
                    }

                    setSelectedColumns([])
                    setFactsColumnRanges(copiedRanges)
                    fireOnSaveEvent(copiedRanges)

                    // Reset the columns highlighted
                    let tabIndexes = []
                    for (let dataIndex = 0; dataIndex < tabData.length; dataIndex++) {
                        let definitionRow = getDefinitionRow(dataIndex, tabData, headerRow)
                        tabIndexes.push(buildFactsHighlightColumns(definitionRow, copiedRanges))
                    }
                    setFactsHighlightColumn(tabIndexes)
                }}>
                    &lt;
                </Button>
            </Grid>
            <Grid item xs={9}>
                {renderTableOrMessageStatingNothingWasSelected()}
                {renderSelectedPreviewDataPanel()}
            </Grid>
        </Grid>
    </>
}

export default FactSourceColumns